import { css, cx, useTheme, FableTheme } from '@fable/theme'
import { useNavigate, useLocation } from 'react-router-dom'
import pluralize from 'pluralize'

import EyeIconCrossedOut from '../../assets/icons/components/EyeIconCrossedOut'
import { FlexBox, TransitionElement, Text } from '@fable/components'
import BookListThumbnail from '../book_list_thumbnail/BookListThumbnail'

import { BookListItem } from '@fable/types'
import { routes as socialRoutes } from '../../app/social/routes'
import { AddNewIcon } from '../../assets/icons/components'
import { useSidebar } from '../../hooks'
import { BOOK_LIST_CREATE } from '../../app/store/constants'

interface Props {
  item: BookListItem
  isAddNewAction: boolean
  index: number
}

export const UserBookList: React.VFC<Props> = ({
  item,
  index,
  isAddNewAction,
}) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { onOpenSidebar } = useSidebar()
  const theme = useTheme()
  const isPrivate = item.privacy !== 'everyone'
  const privacyIcon = !isAddNewAction && isPrivate && (
    <div>
      <EyeIconCrossedOut />
    </div>
  )

  const styles = getStyles(theme, isAddNewAction, isPrivate)

  const onClickItem = () => {
    if (isAddNewAction) return onOpenSidebar(BOOK_LIST_CREATE)

    return navigate(`${socialRoutes.bookList}/${item.id}`, {
      state: { prevPath: pathname },
    })
  }

  return (
    <div key={index} className={styles.item} onClick={onClickItem}>
      <TransitionElement index={index} transitionType="staggerUp">
        {isAddNewAction ? (
          <div className={cx(styles.addNewIcon, styles.itemThumbnail)}>
            <AddNewIcon />
          </div>
        ) : (
          <BookListThumbnail
            bookListData={item}
            className={styles.itemThumbnail}
          />
        )}
        <div className={styles.itemDetail}>
          <FlexBox justifyContent="space-between">
            <Text
              type="subhead"
              sizing={{ base: 'XS', mobileL: 'S' }}
              className={styles.itemName}
            >
              {item.name}
            </Text>
            {privacyIcon}
          </FlexBox>
          <Text
            className={styles.itemLabel}
            type="body"
            sizing="S"
            color={
              isAddNewAction ? theme.colors.greyNeutral : theme.colors.hurricane
            }
          >
            {isAddNewAction
              ? item.description
              : pluralize('book', item.count, true)}
          </Text>
        </div>
      </TransitionElement>
    </div>
  )
}

const getStyles = (
  { colors, motion }: FableTheme,
  isAddNewAction: boolean,
  isPrivate: boolean
) => ({
  item: css`
    cursor: pointer;
    &:after {
      display: none;
    }
  `,
  itemThumbnail: css`
    ${motion.transition()};
    &:hover {
      ${motion.moveY(-15)}
    }
  `,
  itemDetail: css`
    margin-top: 8px;
  `,
  itemName: css`
    color: ${colors.blackSwan};
    font-weight: 600;
  `,
  itemLabel: css`
    display: block;
    margin-top: ${!isAddNewAction && isPrivate ? '-3px' : '0'};
  `,
  addNewIcon: css`
    border-radius: 8px;
    overflow: hidden;

    svg {
      display: block;
    }
  `,
})
